import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Send, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROFILES = [
  { id: "acquereur", label: "Acquéreur", sub: "Je cherche un logement" },
  { id: "investisseur", label: "Investisseur", sub: "DFI, fonds, family office" },
  { id: "partenaire", label: "Partenaire", sub: "BTP, architecte, foncier" },
  { id: "presse", label: "Presse", sub: "Journaliste, média" },
];

const POLE_OPTIONS = [
  "Angré-Ahoué", "Dabou-Assiekro", "Bingerville-Eloka", "Grand-Bassam",
  "Bouaké Centre", "Yamoussoukro", "Korhogo-Karakoro", "San-Pedro",
];

const ContactForm = () => {
  const [profile, setProfile] = useState("acquereur");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    organization: "",
    pole_interest: "",
    segment_interest: "",
    message: "",
  });

  const handleChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.email.trim()) {
      toast.error("Nom et email requis");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        profile,
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        phone: form.phone || null,
        organization: form.organization || null,
        pole_interest: form.pole_interest || null,
        segment_interest: form.segment_interest || null,
        message: form.message || null,
      };
      await axios.post(`${API}/leads`, payload);
      setSubmitted(true);
      toast.success("Votre demande a été transmise. Un conseiller vous recontactera.");
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "Une erreur est survenue.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-[#0C2340] text-white py-24 md:py-40 overflow-hidden"
    >
      {/* Diamond decor */}
      <div className="absolute -top-24 -left-24 w-96 h-96 rotate-45 border border-white/8 pointer-events-none" />
      <div className="absolute -bottom-24 -right-24 w-[500px] h-[500px] rotate-45 border border-[#E8611A]/15 pointer-events-none" />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-16">
        <div className="flex items-center gap-3 mb-8">
          <span className="kidh-diamond" />
          <span className="font-body text-[0.7rem] uppercase tracking-[0.32em] text-white/60">
            Prenons contact
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-16">
          <h2
            data-testid="contact-title"
            className="lg:col-span-8 font-heading font-black text-white text-4xl md:text-6xl leading-[0.95] tracking-tighter"
          >
            Écrivons ensemble<br />
            <span className="text-[#E8611A]">la suite.</span>
          </h2>
          <p className="lg:col-span-4 font-body text-white/70 text-base md:text-lg leading-relaxed">
            Un formulaire, quatre profils. Nous adaptons notre réponse à
            votre projet — acquisition, investissement, partenariat ou presse.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Profile switcher */}
          <div className="lg:col-span-4 flex flex-col gap-2">
            {PROFILES.map((p) => (
              <button
                key={p.id}
                onClick={() => setProfile(p.id)}
                data-testid={`profile-${p.id}`}
                className={`text-left border-l-2 px-5 py-4 transition-all duration-500 ${
                  profile === p.id
                    ? "border-[#E8611A] bg-white/[0.04]"
                    : "border-white/15 hover:border-white/40"
                }`}
              >
                <div className={`font-heading font-black text-xl md:text-2xl tracking-tight ${
                  profile === p.id ? "text-white" : "text-white/60"
                }`}>
                  {p.label}
                </div>
                <div className="font-body text-white/50 text-xs md:text-sm mt-1">
                  {p.sub}
                </div>
              </button>
            ))}

            <div className="mt-8 space-y-3 text-white/70">
              <div>
                <div className="font-body text-[0.6rem] uppercase tracking-[0.28em] text-white/50 mb-1">Adresse</div>
                <div className="font-body text-sm">RCI - Abidjan, Cocody 2 Plateaux</div>
              </div>
              <div>
                <div className="font-body text-[0.6rem] uppercase tracking-[0.28em] text-white/50 mb-1">Email</div>
                <div className="font-body text-sm">contact@kidh.ci</div>
              </div>
              <div>
                <div className="font-body text-[0.6rem] uppercase tracking-[0.28em] text-white/50 mb-1">Téléphone</div>
                <div className="font-body text-sm">+225 27 22 01 01 20</div>
                <div className="font-body text-sm">+225 05 55 511 462</div>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-8 bg-[#0A1C33] p-8 md:p-12 border border-white/10">
            {submitted ? (
              <div
                data-testid="contact-success"
                className="flex flex-col items-start justify-center min-h-[400px] gap-4"
              >
                <CheckCircle2 size={48} className="text-[#E8611A]" />
                <h3 className="font-heading font-black text-white text-3xl md:text-4xl tracking-tighter">
                  Merci, {form.full_name.split(" ")[0]}.
                </h3>
                <p className="font-body text-white/70 max-w-md">
                  Votre demande a bien été transmise à nos équipes. Un conseiller
                  KIDH vous recontactera sous 48 h ouvrées.
                </p>
                <button
                  onClick={() => {
                    setSubmitted(false);
                    setForm({ full_name: "", email: "", phone: "", organization: "", pole_interest: "", segment_interest: "", message: "" });
                  }}
                  className="mt-6 font-body text-[0.7rem] uppercase tracking-[0.24em] text-[#E8611A] hover:underline"
                >
                  Envoyer une autre demande →
                </button>
              </div>
            ) : (
            <form onSubmit={handleSubmit} className="space-y-8" data-testid="contact-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8">
                <div>
                  <label className="font-body text-[0.65rem] uppercase tracking-[0.24em] text-white/50 block mb-2">Nom complet *</label>
                  <input
                    type="text"
                    required
                    value={form.full_name}
                    onChange={handleChange("full_name")}
                    data-testid="input-full-name"
                    className="w-full bg-transparent border-b-2 border-white/15 py-3 px-0 font-body text-white placeholder-white/30 focus:outline-none focus:border-[#E8611A] transition-colors"
                    placeholder="Ex. Aminata Koné"
                  />
                </div>
                <div>
                  <label className="font-body text-[0.65rem] uppercase tracking-[0.24em] text-white/50 block mb-2">Email *</label>
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={handleChange("email")}
                    data-testid="input-email"
                    className="w-full bg-transparent border-b-2 border-white/15 py-3 px-0 font-body text-white placeholder-white/30 focus:outline-none focus:border-[#E8611A] transition-colors"
                    placeholder="votre@email.com"
                  />
                </div>
                <div>
                  <label className="font-body text-[0.65rem] uppercase tracking-[0.24em] text-white/50 block mb-2">Téléphone</label>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={handleChange("phone")}
                    data-testid="input-phone"
                    className="w-full bg-transparent border-b-2 border-white/15 py-3 px-0 font-body text-white placeholder-white/30 focus:outline-none focus:border-[#E8611A] transition-colors"
                    placeholder="+225 …"
                  />
                </div>

                {(profile === "investisseur" || profile === "partenaire" || profile === "presse") && (
                  <div>
                    <label className="font-body text-[0.65rem] uppercase tracking-[0.24em] text-white/50 block mb-2">
                      {profile === "presse" ? "Média" : "Organisation"}
                    </label>
                    <input
                      type="text"
                      value={form.organization}
                      onChange={handleChange("organization")}
                      data-testid="input-organization"
                      className="w-full bg-transparent border-b-2 border-white/15 py-3 px-0 font-body text-white placeholder-white/30 focus:outline-none focus:border-[#E8611A] transition-colors"
                      placeholder="Nom de la structure"
                    />
                  </div>
                )}

                {profile === "acquereur" && (
                  <>
                    <div>
                      <label className="font-body text-[0.65rem] uppercase tracking-[0.24em] text-white/50 block mb-2">Pôle d'intérêt</label>
                      <select
                        value={form.pole_interest}
                        onChange={handleChange("pole_interest")}
                        data-testid="input-pole"
                        className="w-full bg-transparent border-b-2 border-white/15 py-3 px-0 font-body text-white focus:outline-none focus:border-[#E8611A] transition-colors"
                      >
                        <option value="" className="bg-[#0A1C33]">— Sélectionnez un pôle —</option>
                        {POLE_OPTIONS.map((p) => (
                          <option key={p} value={p} className="bg-[#0A1C33]">{p}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="font-body text-[0.65rem] uppercase tracking-[0.24em] text-white/50 block mb-2">Segment</label>
                      <select
                        value={form.segment_interest}
                        onChange={handleChange("segment_interest")}
                        data-testid="input-segment"
                        className="w-full bg-transparent border-b-2 border-white/15 py-3 px-0 font-body text-white focus:outline-none focus:border-[#E8611A] transition-colors"
                      >
                        <option value="" className="bg-[#0A1C33]">— Choisir un segment —</option>
                        <option value="social" className="bg-[#0A1C33]">Social</option>
                        <option value="eco" className="bg-[#0A1C33]">Économique</option>
                        <option value="standing" className="bg-[#0A1C33]">Standing</option>
                        <option value="premium" className="bg-[#0A1C33]">Premium</option>
                      </select>
                    </div>
                  </>
                )}
              </div>

              <div>
                <label className="font-body text-[0.65rem] uppercase tracking-[0.24em] text-white/50 block mb-2">Message</label>
                <textarea
                  rows={4}
                  value={form.message}
                  onChange={handleChange("message")}
                  data-testid="input-message"
                  className="w-full bg-transparent border-b-2 border-white/15 py-3 px-0 font-body text-white placeholder-white/30 focus:outline-none focus:border-[#E8611A] transition-colors resize-none"
                  placeholder="Dites-nous en plus sur votre projet…"
                />
              </div>

              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 pt-4">
                <p className="font-body text-white/40 text-xs max-w-md">
                  En soumettant ce formulaire, vous acceptez d'être recontacté par KIDH. Vos données ne seront jamais partagées avec des tiers.
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit"
                  className="kidh-btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" /> Envoi…
                    </>
                  ) : (
                    <>
                      Envoyer ma demande <Send size={14} />
                    </>
                  )}
                </button>
              </div>
            </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
